import type { StepPageBlock } from '@template-project/funnels-contract';

/**
 * Raw block shape from Go API / backoffice persistence. Fidelity fields are
 * typed loosely (`unknown`) because this normalizer sits at a cross-boundary
 * seam where inputs range from backoffice `ComponentInstance` (typed
 * `StyleProperties`, etc.) to untyped JSON from the API.
 */
export interface RawPageBlock {
  id?: string;
  type?: string;
  definitionId?: string;
  props?: Record<string, unknown>;
  layout?: unknown;
  events?: unknown;
  styles?: unknown;
  advanced?: unknown;
  responsiveOverrides?: unknown;
  children?: RawPageBlock[];
}

/**
 * Canonical block normalizer — single source for adapter + preview parity (ADR 0005).
 */
export function normalizePageBlock(raw: RawPageBlock): StepPageBlock {
  return {
    id: String(raw.id ?? ''),
    type: String(raw.definitionId ?? raw.type ?? 'unknown'),
    props: (raw.props ?? {}) as Record<string, unknown>,
    ...(raw.layout ? { layout: raw.layout as StepPageBlock['layout'] } : {}),
    ...(raw.events ? { events: raw.events as StepPageBlock['events'] } : {}),
    ...(raw.styles ? { styles: raw.styles as StepPageBlock['styles'] } : {}),
    ...(raw.advanced ? { advanced: raw.advanced as StepPageBlock['advanced'] } : {}),
    ...(raw.responsiveOverrides
      ? { responsiveOverrides: raw.responsiveOverrides as StepPageBlock['responsiveOverrides'] }
      : {}),
    ...(Array.isArray(raw.children)
      ? { children: raw.children.map(normalizePageBlock) }
      : {}),
  };
}

export function normalizePageBlocks(rawBlocks: RawPageBlock[] | null | undefined): StepPageBlock[] | null {
  if (!rawBlocks?.length) return null;
  return rawBlocks.map(normalizePageBlock);
}

export function extractBlocksFromContent(
  rawContent: unknown,
): RawPageBlock[] | null {
  // JSONB columns can round-trip as a serialized string from the Go API.
  if (typeof rawContent === 'string') {
    const trimmed = rawContent.trim();
    if (trimmed === '' || trimmed === 'null') return null;
    try {
      return extractBlocksFromContent(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (Array.isArray(rawContent)) {
    return rawContent as RawPageBlock[];
  }
  if (rawContent && typeof rawContent === 'object') {
    const envelope = rawContent as {
      blocks?: RawPageBlock[];
      components?: RawPageBlock[];
      tree?: RawPageBlock[];
    };
    if (Array.isArray(envelope.blocks)) return envelope.blocks;
    if (Array.isArray(envelope.components)) return envelope.components;
    if (Array.isArray(envelope.tree)) return envelope.tree;
  }
  return null;
}

/** Map a backoffice ComponentInstance to a canonical page block. */
export function instanceToPageBlock(inst: {
  id: string;
  definitionId: string;
  props?: Record<string, unknown>;
  events?: unknown;
  styles?: unknown;
  advanced?: unknown;
  responsiveOverrides?: unknown;
  children?: RawPageBlock[];
  layout?: unknown;
}, index = 0): StepPageBlock {
  return normalizePageBlock({
    id: inst.id,
    definitionId: inst.definitionId,
    props: inst.props,
    events: inst.events,
    styles: inst.styles,
    advanced: inst.advanced,
    responsiveOverrides: inst.responsiveOverrides,
    children: inst.children,
    layout: inst.layout ?? {
      column: 1,
      row: index + 1,
      column_span: 12,
      row_span: 1,
      order: index,
    },
  });
}
